import React, {forwardRef} from 'react'

interface SearchInputProps {
  value: string
  placeholder: string
  onChange: (value: string) => void
  onSubmit: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onFocus?: () => void
  disabled?: boolean
  // Combobox a11y (PR 6) — wired by the container.
  expanded?: boolean
  controlsId?: string
  activeDescendantId?: string
  busy?: boolean
}

const MAX_LENGTH = 120

/**
 * Controlled search text input (GS-01) with combobox semantics (GS a11y). Placeholder adapts to the
 * selected type. Enter submits; other keys bubble via onKeyDown so the container drives dropdown
 * arrow-key navigation and Escape.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({value, placeholder, onChange, onSubmit, onKeyDown, onFocus, disabled, expanded, controlsId, activeDescendantId, busy}, ref) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return
      onKeyDown?.(e)
      if (e.defaultPrevented) return
      if (e.key === 'Enter') {
        e.preventDefault()
        onSubmit()
      }
    }

    return (
      <input
        ref={ref}
        type='text'
        className='nb-gs__input'
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        maxLength={MAX_LENGTH}
        autoComplete='off'
        spellCheck={false}
        role='combobox'
        aria-label='Search'
        aria-autocomplete='list'
        aria-expanded={!!expanded}
        aria-controls={controlsId}
        aria-activedescendant={activeDescendantId || undefined}
        aria-busy={busy || undefined}
        onChange={(e) => !disabled && onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => !disabled && onFocus?.()}
        style={disabled ? {cursor: 'not-allowed', opacity: 0.6} : undefined}
      />
    )
  }
)

SearchInput.displayName = 'SearchInput'
